import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Helper for react-easy-crop to get cropped image as a Blob
export async function getCroppedImg(
  imageSrc: string,
  crop: { x: number; y: number; width: number; height: number },
  rotation = 0
): Promise<Blob | null> {
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new window.Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const safeArea = Math.max(image.width, image.height) * 2;
  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(image, (safeArea - image.width) / 2, (safeArea - image.height) / 2);

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // set canvas to final desired crop size
  canvas.width = crop.width;
  canvas.height = crop.height;

  // draw cropped image
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - crop.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - crop.y)
  );

  // return as blob
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, 'image/jpeg');
  });
}

// Geocode an address to lat/lng using OpenStreetMap Nominatim API (free, no API key required)
export async function geocodeAddress(address: string, apiKey?: string): Promise<{lat: number, lng: number} | null> {
  try {
    // Use our proxy endpoint instead of direct Nominatim calls
    const searchParams = new URLSearchParams({
      q: address,
      limit: '1',
      countrycodes: 'us,ca'
    });
    
    const response = await fetch(`/api/geocoding/search?${searchParams.toString()}`);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (Array.isArray(data) && data.length > 0) {
      const result = data[0];
      return { 
        lat: parseFloat(result.lat), 
        lng: parseFloat(result.lon) 
      };
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Format lot size for display based on unit
 * @param lotSize - The lot size value
 * @param unit - The unit ('acres', 'squareFeet', etc.)
 * @returns Formatted string like "0.366 acres" or "8,233 sqft"
 */
export function formatLotSize(lotSize: number, unit: string): string {
  if (!lotSize || lotSize <= 0) return 'N/A';
  
  switch (unit?.toLowerCase()) {
    case 'acres':
      // For acres, show up to 3 decimal places
      return `${lotSize.toFixed(3).replace(/\.?0+$/, '')} acres`;
    
    case 'squarefeet':
    case 'sqft':
    case 'square feet':
      // For square feet, format with commas and show as sqft
      return `${Math.round(lotSize).toLocaleString()} sqft`;
    
    default:
      // For unknown units, just show the value and unit
      return `${lotSize} ${unit || 'units'}`;
  }
}

/**
 * Convert lot size to square feet for consistent calculations
 * @param lotSize - The lot size value
 * @param unit - The unit ('acres', 'squareFeet', etc.)
 * @returns Lot size in square feet
 */
export function convertLotSizeToSquareFeet(lotSize: number, unit: string): number {
  if (!lotSize || lotSize <= 0) return 0;
  
  switch (unit?.toLowerCase()) {
    case 'acres':
      return lotSize * 43560; // 1 acre = 43,560 square feet
    
    case 'squarefeet':
    case 'sqft':
    case 'square feet':
      return lotSize;
    
    default:
      return lotSize; // Return as-is for unknown units
  }
}

// Reverse geocode coordinates to get detailed address using OpenStreetMap Nominatim
export async function reverseGeocode(lat: number, lng: number): Promise<{city: string, state: string, fullAddress: string, streetAddress: string, postalCode: string, displayName: string} | null> {
  try {
    // Use our proxy endpoint instead of direct Nominatim calls
    const searchParams = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString()
    });
    
    const url = `/api/geocoding/reverse?${searchParams.toString()}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (data && (data.city || data.state || data.fullAddress)) {
      const result = {
        city: data.city || '',
        state: data.state || '',
        fullAddress: data.fullAddress || '',
        streetAddress: data.streetAddress || '',
        postalCode: data.postalCode || '',
        displayName: data.displayName || ''
      };
      return result;
    }
    
    return null;
  } catch (error) {
    return null;
  }
}

// Get drive times (in minutes) from multiple origins to a single destination using OSRM
// OSRM is a free, open-source routing service that works with OpenStreetMap data
export async function getDriveTimes(
  origins: {lat: number, lng: number}[],
  destination: {lat: number, lng: number}
): Promise<number[]> {
  try {
    // OSRM table service endpoint (free, no API key required)
    const baseUrl = 'https://router.project-osrm.org/table/v1/driving';
    
    // Format coordinates as "lng,lat" for OSRM
    const coordinates = [
      ...origins.map(origin => `${origin.lng},${origin.lat}`),
      `${destination.lng},${destination.lat}`
    ].join(';');
    
    // Create sources array (all origins) and destinations array (just the destination)
    const sources = Array.from({length: origins.length}, (_, i) => i).join(';');
    const destinations = origins.length.toString(); // Index of the destination
    
    const url = `${baseUrl}/${coordinates}?sources=${sources}&destinations=${destinations}&annotations=duration`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'MyAgentApp/1.0'
      }
    });
    
    if (!response.ok) {
      throw new Error(`OSRM API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.durations && data.durations.length > 0) {
      // Convert seconds to minutes and return only the durations for origins to destination
      const driveTimes = data.durations.map((duration: number) => Math.round(duration / 60));
      return driveTimes;
    }
    
    // Fallback to distance-based estimation if OSRM fails
    const fallbackTimes = origins.map(origin => {
      const distance = haversineDistance(origin.lat, origin.lng, destination.lat, destination.lng);
      return Math.round((distance / 30) * 60); // 30 mph average speed
    });
    return fallbackTimes;
    
  } catch (error) {
    // Fallback to distance-based estimation
    const fallbackTimes = origins.map(origin => {
      const distance = haversineDistance(origin.lat, origin.lng, destination.lat, destination.lng);
      return Math.round((distance / 30) * 60); // 30 mph average speed
    });
    return fallbackTimes;
  }
}

// Address autocomplete using OpenStreetMap Nominatim
export interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
  type: string;
  importance: number;
}

export async function getAddressSuggestions(
  query: string,
  limit: number = 5
): Promise<AddressSuggestion[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  // Add some common address patterns and known locations
  const commonAddresses = getCommonAddressSuggestions(query);
  if (commonAddresses.length > 0) {
    return commonAddresses.slice(0, limit);
  }

  // Try multiple search strategies using our proxy endpoint
  const searchStrategies = [
    // Strategy 1: US-focused search with viewbox
    {
      name: 'US-focused',
      params: {
        q: query,
        limit: limit,
        countrycodes: 'us',
        viewbox: '-125,25,-66,49',
        bounded: '1'
      }
    },
    // Strategy 2: US and Canada with viewbox
    {
      name: 'North America',
      params: {
        q: query,
        limit: limit,
        countrycodes: 'us,ca',
        viewbox: '-125,25,-66,49',
        bounded: '1'
      }
    },
    // Strategy 3: Global search with country codes
    {
      name: 'Global with countries',
      params: {
        q: query,
        limit: limit,
        countrycodes: 'us,ca,gb,au'
      }
    },
    // Strategy 4: Basic global search (fallback)
    {
      name: 'Global basic',
      params: {
        q: query,
        limit: limit
      }
    }
  ];

  for (const strategy of searchStrategies) {
    try {
      // Build query string for our proxy endpoint
      const searchParams = new URLSearchParams(strategy.params);
      const proxyUrl = `/api/geocoding/search?${searchParams.toString()}`;

      // Use a simple timeout instead of AbortController to avoid conflicts
      const response = await Promise.race([
        fetch(proxyUrl),
        new Promise<Response>((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        )
      ]);
      
      if (!response.ok) {
        continue;
      }
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        // Deduplicate results based on display_name and coordinates
        const uniqueResults = data.filter((item: any, index: number, self: any[]) => 
          index === self.findIndex((t: any) => 
            t.display_name === item.display_name && 
            t.lat === item.lat && 
            t.lon === item.lon
          )
        );
        return uniqueResults;
      }
      
    } catch (error) {
      continue;
    }
  }
  
  return [];
}

// Helper function to provide common address suggestions
function getCommonAddressSuggestions(query: string): AddressSuggestion[] {
  const lowerQuery = query.toLowerCase().trim();
  
  // Common US addresses and landmarks
  const commonAddresses: { [key: string]: AddressSuggestion } = {
    'white house': {
      display_name: '1600 Pennsylvania Avenue NW, Washington, DC 20500, United States',
      lat: '38.8977',
      lon: '-77.0365',
      place_id: 1,
      type: 'government',
      importance: 0.9,
      class: 'government'
    },
    '1600 pennsylvania': {
      display_name: '1600 Pennsylvania Avenue NW, Washington, DC 20500, United States',
      lat: '38.8977',
      lon: '-77.0365',
      place_id: 1,
      type: 'government',
      importance: 0.9,
      class: 'government'
    },
    'empire state': {
      display_name: 'Empire State Building, 350 5th Ave, New York, NY 10118, United States',
      lat: '40.7484',
      lon: '-73.9857',
      place_id: 2,
      type: 'building',
      importance: 0.8,
      class: 'building'
    },
    'times square': {
      display_name: 'Times Square, New York, NY 10036, United States',
      lat: '40.7580',
      lon: '-73.9855',
      place_id: 3,
      type: 'attraction',
      importance: 0.8,
      class: 'tourism'
    },
    'golden gate': {
      display_name: 'Golden Gate Bridge, San Francisco, CA 94129, United States',
      lat: '37.8199',
      lon: '-122.4783',
      place_id: 4,
      type: 'bridge',
      importance: 0.8,
      class: 'man_made'
    },
    'apple park': {
      display_name: 'Apple Park, 1 Apple Park Way, Cupertino, CA 95014, United States',
      lat: '37.3346',
      lon: '-122.0090',
      place_id: 5,
      type: 'office',
      importance: 0.7,
      class: 'office'
    }
  };

  // Check for exact matches first
  if (commonAddresses[lowerQuery]) {
    return [commonAddresses[lowerQuery]];
  }

  // Check for partial matches
  const matches = Object.entries(commonAddresses)
    .filter(([key]) => key.includes(lowerQuery) || lowerQuery.includes(key))
    .map(([, value]) => value)
    .sort((a, b) => b.importance - a.importance);

  return matches;
}

// Debounce function for autocomplete
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Calculate the great-circle distance between two points (Haversine formula)
export function haversineDistance(
  lat1: number, lon1: number, lat2: number, lon2: number
): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 3958.8; // Radius of Earth in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Filter out generic Zillow descriptions that contain "Zestimate"
 * @param description - The property description to check
 * @returns The original description if valid, or empty string if it contains "Zestimate"
 */
export function filterZestimateDescriptions(description: string | null | undefined): string {
  if (!description) return '';
  
  // Hide any description that contains "Zestimate" or "Zillow" (case-insensitive)
  const lower = description.toLowerCase();
  if (lower.includes('zestimate') || lower.includes('zillow')) {
    return '';
  }
  
  return description;
}

/**
 * Format price for map pin display in $425K format
 * @param price - The price value to format
 * @returns Formatted price string like "$425K" or "$1.2M"
 */
export function formatPriceForMapPin(price: number | null | undefined): string {
  if (!price || price <= 0) return '';
  
  if (price >= 1000000) {
    const millions = price / 1000000;
    return `$${millions.toFixed(1).replace(/\.0$/, '')}M`;
  } else if (price >= 1000) {
    const thousands = price / 1000;
    return `$${thousands.toFixed(0)}K`;
  } else {
    return `$${price.toLocaleString()}`;
  }
}

/**
 * Format a price range for map pin display, using compact units
 * @param minPrice - Minimum price (optional)
 * @param maxPrice - Maximum price (optional)
 */
export function formatPriceRangeForMapPin(minPrice?: number | null, maxPrice?: number | null): string {
  const hasMin = typeof minPrice === 'number' && minPrice > 0;
  const hasMax = typeof maxPrice === 'number' && maxPrice > 0;
  if (hasMin && hasMax) {
    if (minPrice === maxPrice) return formatPriceForMapPin(minPrice as number);
    return `${formatPriceForMapPin(minPrice as number)} - ${formatPriceForMapPin(maxPrice as number)}`;
  }
  if (hasMin) return formatPriceForMapPin(minPrice as number);
  if (hasMax) return formatPriceForMapPin(maxPrice as number);
  return '';
}
